import { useCallback, useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router';
import { useAuthStore } from '../auth/authStore.js';
import { startSessionKeeper } from '../auth/sessionKeeper.js';
import { useChatStore } from '../chat/chatStore.js';
import { useDirectoryStore } from '../chat/directoryStore.js';
import { resetAcknowledgements } from '../chat/receiptAcks.js';
import { useChatSocket } from '../chat/useChatSocket.js';
import { useUploadStore } from '../files/uploadStore.js';
import { useUnreadTitle } from '../notifications/useUnreadTitle.js';
import { AccountMenu } from './AccountMenu.js';
import { ChannelList } from './ChannelList.js';
import { ConnectionStatus } from './ConnectionStatus.js';
import { Icon } from './Icon.js';
import { Logo } from './Logo.js';
import { NewConversation } from './NewConversation.js';
import { NotificationBell } from './NotificationBell.js';
import { NotificationPrompt } from './NotificationPrompt.js';

/**
 * The signed-in shell: header, conversation rail, and a slot for the current view.
 *
 * `useChatSocket` lives here rather than in the chat page so that messages and
 * presence keep arriving for every channel, not just the one on screen.
 */
export function AppLayout() {
  const logout = useAuthStore((state) => state.logout);
  const resetChat = useChatStore((state) => state.reset);
  const resetUploads = useUploadStore((state) => state.reset);
  const resetDirectory = useDirectoryStore((state) => state.reset);
  const remember = useDirectoryStore((state) => state.remember);
  const self = useAuthStore((state) => state.user);

  const [railOpen, setRailOpen] = useState(false);

  useChatSocket();
  // The signal that needs no permission and no secure context: a hidden tab
  // still shows its unread count in the tab strip.
  useUnreadTitle();

  // Refreshes the token while the app is in use and signs out the moment the
  // session ends, rather than on the next failed request.
  useEffect(() => startSessionKeeper(), []);

  // The directory is where avatars look people up - including the user's own.
  useEffect(() => {
    if (self) {
      remember([
        { id: self.id, displayName: self.displayName, avatarUpdatedAt: self.avatarUpdatedAt },
      ]);
    }
  }, [self, remember]);

  // On a phone the rail is a drawer over the conversation, and navigating is the
  // signal that the user is done with it.
  //
  // Closed by the things that navigate, rather than by watching the location.
  // Reacting to a path change means updating state while the router is already
  // re-rendering this subtree - either from an effect (which the
  // `set-state-in-effect` rule rejects) or during render, which is a second
  // render pass interleaved with the one that swaps the conversation. Neither is
  // worth it for a control that only exists below 900px.
  const closeRail = useCallback(() => setRailOpen(false), []);

  // Signing out must not leave one account's conversations, uploads or
  // resolved names in memory for the next.
  useEffect(
    () => () => {
      resetChat();
      resetUploads();
      resetDirectory();
      resetAcknowledgements();
    },
    [resetChat, resetUploads, resetDirectory],
  );

  return (
    <div className="shell">
      <header className="shell-header">
        <div className="shell-header-left">
          <button
            type="button"
            className="btn btn--icon rail-toggle"
            aria-label={railOpen ? 'Hide conversations' : 'Show conversations'}
            aria-expanded={railOpen}
            onClick={() => setRailOpen((open) => !open)}
          >
            <Icon name={railOpen ? 'x' : 'menu'} size={18} />
          </button>

          <Link to="/" className="brand" onClick={closeRail}>
            <Logo size={26} />
          </Link>
        </div>

        <div className="shell-header-right">
          <ConnectionStatus />
          <NotificationBell />
          {/* Wrapped: `logout` takes an optional notice, and must not be handed the click event. */}
          <AccountMenu onSignOut={() => logout()} />
        </div>
      </header>

      <NotificationPrompt />

      <div className="shell-body">
        {railOpen ? (
          <button
            type="button"
            className="rail-scrim"
            aria-label="Close conversations"
            onClick={closeRail}
          />
        ) : null}

        <nav
          className={railOpen ? 'shell-rail shell-rail--open' : 'shell-rail'}
          aria-label="Conversations"
        >
          <NewConversation onNavigate={closeRail} />
          <ChannelList onNavigate={closeRail} />
        </nav>

        <main className="shell-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
